import dayjs from 'dayjs'
import UTC from 'dayjs/plugin/utc'
import BigNumber from 'bignumber.js'
import { isBrowser } from '../componentsNew/_util/is'

dayjs.extend(UTC)

type FormatType = 'MM/DD/YYYY HH:mm:ss' | 'MM/DD HH:mm' | 'YYYY.MM.DD HH:mm UTC' | (string & NonNullable<unknown>)

/**
 * @name 导入静态资源 <Vite>
 * @param value
 * @returns
 */
export function importUri(value: string) {
  const result = value.replace('@/', '')
  return new URL(`/src/${result}`, import.meta.url).href
}

/**
 * @name UTC时间格式化
 * @param value
 * @param format
 * @returns
 */
export const filterTime = (value: string | number, format: FormatType = 'MM/DD HH:mm') => {
  const stamp = typeof value === 'number' ? value : Number(value)
  return dayjs.utc(stamp).local().format(format)
}

/**
 * @name UTC(Unix时间戳)格式化
 * @param value Unix时间戳
 * @param format
 * @returns
 */
export const filterUnixTime = (value: string | number, format: FormatType = 'MM/DD HH:mm') => {
  const stamp = typeof value === 'number' ? value : Number(value)
  return dayjs.utc(stamp).format(format)
}

/**
 * @name 数字精度 -> decimalPlaces[does not retain trailing zeros]
 * @param value
 * @param decimal
 * @returns
 */
export const filterPrecision = (value: string | number, decimal = 4) => {
  const result = new BigNumber(value).toFixed(decimal, BigNumber.ROUND_DOWN).toString()
  return result
}

/**
 * @name 数字精度，保留小数点后4位,并处理非法数字
 * @param value
 * @param decimal
 * @returns
 */
export const filterPrecisionAndIllegalNumber = (value?: string | number, decimal = 4) => {
  if (value === undefined || value === null) return '--'
  return filterPrecision(value, decimal)
}

/**
 * @name 显示有效数字
 * @param value
 */
export function filterSignificantDigits(value: string | number): string {
  return new BigNumber(value).toString()
}

/**
 * @name 数字千位分割
 * @param value
 * @param decimal
 * @param isCutZero
 * @returns
 */
export const filterThousands = (value: string | number, decimal = 4, isCutZero?: boolean) => {
  if (new BigNumber(value).isNaN()) return `${value}`
  if (isCutZero) return new BigNumber(value).decimalPlaces(decimal, BigNumber.ROUND_DOWN).toFormat()
  const result = new BigNumber(filterPrecision(value, decimal)).toFormat(decimal, BigNumber.ROUND_DOWN)
  return result
}

/**
 * @name 隐藏文本信息
 * @param value
 * @param before
 * @param after
 * @param fuzz
 * @returns
 */
export const filterHideText = (value: string, before = 4, after = 4, fuzz = '****') => {
  if (!value || value.length <= before + after) return value
  return `${value.slice(0, before)}${fuzz}${value.slice(-after)}`
}

/**
 * @name 最大值过滤
 * @param value
 * @param max
 * @param decimal
 * @returns
 */
export const filterMaxNumber = (value: string, max = '0', decimal = 4) => {
  if (new BigNumber(value).isNaN() || /^[0-9]\d*\.$/.test(value)) return value
  const result = BigNumber.minimum(value, filterPrecision(max, decimal)).toString()
  return result
}

/**
 * @name 最小精度显示优化
 * @param value
 * @param decimal
 * @param prefix
 * @returns
 */
export const filterMinimumPrecision = (value: string, decimal = 4, prefix = '<') => {
  if (Number(value) === 0) return '0'
  const minimum = `0.${'1'.padStart(decimal, '0')}`
  const compare = new BigNumber(value).isGreaterThan(minimum)
  const result = compare ? filterThousands(value, decimal) : `${prefix}${minimum}`
  return result
}

/**
 * @name 大数缩写 -> K M B
 * @param value
 * @param decimal
 * @returns
 */
export const filterAbbreviation = (value: string | number, decimal = 2) => {
  const numValue = new BigNumber(value)
  if (numValue.isGreaterThanOrEqualTo(1e9)) {
    return `${filterThousands(numValue.dividedBy(1e9).toString(), decimal)}B`
  }
  if (numValue.isGreaterThanOrEqualTo(1e6)) {
    return `${filterThousands(numValue.dividedBy(1e6).toString(), decimal)}M`
  }
  if (numValue.isGreaterThanOrEqualTo(1e3)) {
    return `${filterThousands(numValue.dividedBy(1e3).toString(), decimal)}K`
  }
  return filterThousands(value, decimal)
}

/**
 * @name 首字母大写
 * @param value
 * @returns
 */
export const filterTitleCase = (value: string) => {
  return value.toLowerCase().replace(/^\S/, (s) => s.toUpperCase())
}

/**
 * @name 非负数校验
 * @param value
 * @param decimal
 * @returns
 */
export const verifyValidNumber = (value: string, decimal = 4) => {
  const regexp = decimal === 0 ? '(^(0|[1-9]\\d*)$)' : `(^(0|([1-9]\\d*))(\\.\\d{0,${decimal}})?$)`
  return !new RegExp(regexp).test(value)
}

/**
 * @name 获取操作系统类型
 * @param userAgent
 * @returns
 */
export const getDeviceOS = (userAgent?: string) => {
  if (!isBrowser() || !userAgent) return 'unknown'
  if (/android/i.test(userAgent)) return 'Android'
  if (/iPad|iPhone|iPod/.test(userAgent)) return 'iOS'
  return 'unknown'
}

/**
 * @name UUID(RFC4122) -> https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid#answer-2117523
 * @returns
 */
export const uuidv4 = () => {
  const UINT36 = '10000000-1000-4000-8000-100000000000'
  // eslint-disable-next-line no-bitwise
  const random = (x: string) => ((Number(x) ^ crypto.getRandomValues(new Uint8Array(1))[0]) & 15) >> (Number(x) / 4)
  return UINT36.replace(/[018]/g, (x) => random(x)?.toString(16))
}

/**
 * @name 外链跳转
 * @param address
 * @param target
 */
export const jumpLink = (address: string, target: '_self' | '_blank' = '_self') => {
  window.open(address, target)
}

/**
 * @name blob下载
 * @param fileName
 * @param content
 * @returns
 */
export const download = (fileName: string, content: Blob, type = 'image/png') => {
  const link = window.document.createElement('a')
  if (typeof link.download === 'undefined') return
  try {
    const blob = new Blob([content], { type })
    link.href = URL.createObjectURL(blob)
    link.download = fileName
    link.style.display = 'none'
    window.document.body.appendChild(link)
    link.click()
  } finally {
    URL.revokeObjectURL(link.href)
    window.document.body.removeChild(link)
  }
}

/**
 * @name 发送谷歌统计
 * @param event
 * @returns
 */
// export const sendGoogleAnalytics = (eventName: string, eventConfig: any = {}) => {
//   const isProduction = import.meta.env.VITE_APP_ENV === 'production'
//   if (!isBrowser || !window.gtag || !isProduction) return
//   const params = {
//     ...eventConfig,
//     timestamp: Date.now(),
//     referer: window.document.referrer || window.location.href,
//   }
//   // console.log('GA_click: ', eventName, params);
//   window.gtag('event', eventName, params)
// }

export function getLocalStorageObj<T>(key: string): T | undefined {
  const data = localStorage.getItem(key)
  if (!data) return undefined
  return JSON.parse(data)
}
